import React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAdminOrganizations } from "../hooks/useAdminOrganizations";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "../components/LoadingSkeleton";
import {
  Building2,
  ChevronRight,
  ShieldAlert,
} from "lucide-react";

export const OrgSettings: React.FC = () => {
  const navigate = useNavigate();
  const { data: adminOrgs, isLoading, error } = useAdminOrganizations();

  if (isLoading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Organization Settings
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage settings for your organizations
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardContent className="p-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <div className="flex gap-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Organization Settings
          </h1>
        </div>
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-6 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 dark:text-red-400">
              Failed to load organizations. Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!adminOrgs || adminOrgs.length === 0) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Organization Settings
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage settings for your organizations
          </p>
        </div>
        <Card className="border-amber-200 dark:border-amber-800">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Admin Access
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              You don't have admin access to any organizations. Contact your
              organization administrator if you believe this is an error.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="h-6 w-6" />
          Organization Settings
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Select an organization to manage its settings
        </p>
      </div>

      <div className="grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
        {adminOrgs.map((org) => (
          <Card
            key={org.id}
            className="overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 group"
            onClick={() =>
              navigate({ to: "/helpdesk/org-settings/$orgId", params: { orgId: org.id } })
            }
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                {/* Left side - Name, Description, and Logo (mobile) */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate text-sm sm:text-base">
                      {org.name}
                    </h3>
                  </div>
                  {org.description ? (
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {org.description}
                    </p>
                  ) : (
                    <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500 italic">
                      No description
                    </p>
                  )}
                  {/* Mobile logo - below description */}
                  {org.logoUrl && (
                    <div className="sm:hidden mt-2 p-1.5 rounded-lg dark:bg-white/35 w-fit">
                      <img
                        src={org.logoUrl}
                        alt={`${org.name} logo`}
                        className="h-8 w-auto object-contain"
                      />
                    </div>
                  )}
                </div>

                {/* Right side - Logo (desktop) and Arrow */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {org.logoUrl && (
                    <div className="hidden sm:block p-2 rounded-lg dark:bg-white/35">
                      <img
                        src={org.logoUrl}
                        alt={`${org.name} logo`}
                        className="h-10 w-auto object-contain"
                      />
                    </div>
                  )}
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
